import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, Plus, Briefcase, Clock, Archive, CheckCircle } from 'lucide-react';
import JobOfferModal from './JobOfferModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ManagerHorizonHomepage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobCounts, setJobCounts] = useState({
    en_cours: 0,
    en_attente: 0,
    archive: 0,
    brouillon: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchJobCounts();
  }, []);

  const fetchJobCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const counts = await response.json();
        setJobCounts(counts);
      } else {
        toast.error('Erreur lors du chargement des compteurs');
      }
    } catch (error) {
      console.error('Error fetching job counts:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleCreateJobOffer = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleJobOfferCreated = () => {
    setShowModal(false);
    fetchJobCounts(); // Refresh counts after creating a job offer
  };

  const handleBlockClick = (blockName, blockId) => {
    if (blockId === 'manager.hs.cours') {
      navigate('/manager-hs/offres-en-cours');
    } else if (blockId === 'manager.hs.attente') {
      navigate('/manager-hs/offres-en-attente');
    } else {
      toast.info(`Navigation vers ${blockName} (${blockId}) - À implémenter`);
    }
  };

  // Horizon Solution logo
  const horizonLogo = 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png';

  return (
    <div className="manager-homepage">
      {/* Header */}
      <header className="rh-header">
        <img
          src={horizonLogo}
          alt="Logo Horizon Solution"
          className="rh-header-logo"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <h1>Recrutement Horizon Solution</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Welcome Section */}
        <div className="rh-welcome">
          <h2>Gestion des Recrutements - Horizon Solution</h2>
          <p>Gérez les offres d'emploi et les candidatures de votre entreprise</p>
        </div>

        {/* Create Job Offer Button */}
        <div className="manager-create-section">
          <Button
            onClick={handleCreateJobOffer}
            className="btn-primary manager-create-btn"
            style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
          >
            <Plus size={20} className="mr-2" />
            Créer une fiche de poste
          </Button>
        </div>

        {/* Job Offer Blocks */}
        <div className="manager-blocks-grid">
          {/* Offres en cours */}
          <Card 
            className="manager-block cursor-pointer group"
            onClick={() => handleBlockClick('Offres en cours', 'manager.hs.cours')}
          >
            <CardContent className="manager-block-content">
              <div className="manager-block-header">
                <div className="manager-block-icon">
                  <Briefcase className="w-8 h-8 text-primary" />
                </div>
                <div className="manager-block-counter">
                  {loading ? '...' : jobCounts.en_cours}
                </div>
              </div>
              <h3>Offres en cours</h3>
              <p>Gestion des différentes offres d'emplois et des candidatures en cours</p>
            </CardContent>
          </Card>

          {/* Offres en attente */}
          <Card 
            className="manager-block cursor-pointer group"
            onClick={() => handleBlockClick('Offres en attente', 'manager.hs.attente')}
          >
            <CardContent className="manager-block-content">
              <div className="manager-block-header">
                <div className="manager-block-icon">
                  <Clock className="w-8 h-8 text-primary" />
                </div>
                <div className="manager-block-counter">
                  {loading ? '...' : jobCounts.en_attente}
                </div>
              </div>
              <h3>Offres en attente</h3>
              <p>Gestion d'offres d'emplois en attente de publication</p>
            </CardContent>
          </Card>

          {/* Offres archivées */}
          <Card 
            className="manager-block cursor-pointer group"
            onClick={() => handleBlockClick('Offres archivées', 'manager.hs.archive')}
          >
            <CardContent className="manager-block-content">
              <div className="manager-block-header">
                <div className="manager-block-icon">
                  <Archive className="w-8 h-8 text-primary" />
                </div>
                <div className="manager-block-counter">
                  {loading ? '...' : jobCounts.archive}
                </div>
              </div>
              <h3>Offres archivées</h3>
              <p>Archives des offres d'emplois terminées</p>
            </CardContent>
          </Card>
        </div>

      </main>

      {/* Job Offer Modal */}
      {showModal && (
        <JobOfferModal
          isOpen={showModal}
          onClose={handleCloseModal}
          onSuccess={handleJobOfferCreated}
          managerType="horizon" // "horizon" for Horizon Solution, "general" for others
        />
      )}
    </div>
  );
};

export default ManagerHorizonHomepage;